/**
 * trendSeries — pure pivot from the §5 resolve envelope to one metric's time series
 * for the trust-enveloped trend chart (A2 tier 1). No fetch (CD-7): reuses the page's
 * single /resolve. Each point keeps its full Actual Cell so the chart draws the
 * envelope (RAG dot, lock shade, DQ, divergence) from server truth, never re-derived.
 */

import type {
  Cell,
  Direction,
  MetricFormat,
  PlanType,
  ResolveResponse,
  Site,
} from '../../types/metrics'
import { isPeriodComplete, periodHeading, scaleForDisplay } from './cellFormat'

export interface TrendPoint {
  period: string
  label: string
  actual: number | null // display-scaled (gbp_k ÷1000, pct ×100) for the axis/line
  target: number | null
  /** This point's Actual is a forward estimate, not a measurement (Cell.forecast).
   *  The charts split the line on it — solid to here, dashed beyond. */
  forecast: boolean
  cell: Cell | null // the Actual envelope for this period (null = not served)
  targetCell: Cell | null
}

export interface TrendSeries {
  metricId: string
  title: string
  site: Site
  format: MetricFormat
  direction: Direction
  points: TrendPoint[]
  lockedFrom: string | null // first locked/divergent period (shade start)
  lockedTo: string | null // last locked/divergent period (shade end)
  dqFlags: string[]
  hasDivergent: boolean
  servedCount: number
}

const keyOf = (metricId: string, site: Site, period: string, plan: PlanType) =>
  `${metricId}|${site}|${period}|${plan}`

/** Display-scale a raw master value through the shared §4 scale (null stays a gap). */
function displayValue(value: number | null, format: MetricFormat): number | null {
  return value === null ? null : scaleForDisplay(value, format)
}

function seriesLookup(resolve: ResolveResponse): Map<string, Cell> {
  const lookup = new Map<string, Cell>()
  for (const cell of resolve.cells) {
    lookup.set(keyOf(cell.metric_id, cell.site, cell.period, cell.plan_type), cell)
  }
  return lookup
}

function toPoint(
  lookup: Map<string, Cell>,
  metricId: string,
  site: Site,
  period: string,
  format: MetricFormat
): TrendPoint {
  const cell = lookup.get(keyOf(metricId, site, period, 'Actual')) ?? null
  const targetCell = lookup.get(keyOf(metricId, site, period, 'Target')) ?? null
  // A month still running plots NOTHING (19 Aug 2026, Mike). Its cell is served and stays
  // on the point — the tooltip and the envelope still carry it — but a part-month drawn at
  // full height reads as a collapse: Group revenue was £710k in July and £4.2k on 19 August,
  // and the line fell off the axis. The TARGET still plots, because a plan for a month that
  // has not finished is exactly what a plan is.
  //
  // A FORECAST IS THE EXCEPTION (23 Aug 2026). The rule was written against a part-month,
  // and a forecast cell is not one: Revenue's August is a whole-month £615k, not 23 days of
  // £183k, and Revenue Firm is a forward order book in every month it has. Blanking those
  // was the rule firing on the wrong thing — so the point plots, and the charts draw it
  // dashed. Cell.forecast is the server's word for it, never re-derived here.
  const running = !isPeriodComplete(period) && !cell?.forecast
  return {
    period,
    label: periodHeading(period),
    actual: running ? null : displayValue(cell?.value ?? null, format),
    target: displayValue(targetCell?.value ?? null, format),
    forecast: Boolean(cell?.forecast),
    cell,
    targetCell,
  }
}

export function buildTrendSeries(
  resolve: ResolveResponse,
  metricId: string,
  site: Site,
  fallbackTitle?: string
): TrendSeries {
  const lookup = seriesLookup(resolve)
  const served = resolve.periods
    .map((period) => lookup.get(keyOf(metricId, site, period, 'Actual')))
    .filter((cell): cell is Cell => cell !== undefined)
  const first = served[0] ?? null
  // Format falls back to a served TARGET when the metric has no actual in the window —
  // a plan-only row (every future month) would otherwise infer 'count' and plot raw
  // pounds against a £k axis. The envelope carries format on both plan types.
  const firstTargeted = resolve.periods
    .map((period) => lookup.get(keyOf(metricId, site, period, 'Target')))
    .find((cell): cell is Cell => cell !== undefined)
  const format = first?.format ?? firstTargeted?.format ?? 'count'
  const points = resolve.periods.map((period) => toPoint(lookup, metricId, site, period, format))
  const locked = points.filter((p) => p.cell !== null && p.cell.lock_state !== 'live')
  return {
    metricId,
    // served_metric when any cell serves; else the catalogue title the matrix shows,
    // never the raw registry id (falls back to it only if the catalogue lacks the row).
    title: first?.served_metric ?? fallbackTitle ?? metricId,
    site,
    format,
    direction: first?.direction ?? 'higher_is_better',
    points,
    lockedFrom: locked.length ? locked[0].period : null,
    lockedTo: locked.length ? locked[locked.length - 1].period : null,
    dqFlags: [...new Set(served.flatMap((cell) => cell.dq_flags))],
    hasDivergent: served.some((cell) => cell.lock_state === 'divergent'),
    servedCount: served.length,
  }
}
